import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { Button, Switch } from "@heroui/react";
import SearchFilter from "../../../utils/SearchFilter";
import {
  JODI_REPORT_ENDPOINTS,
  createJodiReportDatasource,
  slNoColumn,
} from "../../../utils/jodiReportServerSide";

ModuleRegistry.registerModules([AllCommunityModule]);

const PAGE_SIZE = 20;

const ViewByUserButton = (props) => {
  const { data } = props;
  const { onViewByUser } = props.context;

  if (!data?.total_users) {
    return <span className="text-gray-400 text-xs">—</span>;
  }

  return (
    <button
      className="bg-indigo-500 text-white px-2 py-1 rounded text-xs"
      onClick={() => onViewByUser(data.number)}
    >
      View By User
    </button>
  );
};

const JodiReportTable = ({ basePath = "/admin" }) => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const searchRef = useRef("");
  const filterByResultRef = useRef(false);
  const filterInitialisedRef = useRef(false);
  const [matchInfo, setMatchInfo] = useState(null);
  const [resultInfo, setResultInfo] = useState(null);
  const [gridReady, setGridReady] = useState(false);
  const [quickFilterText, setQuickFilterText] = useState("");
  const [filterByResult, setFilterByResult] = useState(false);

  const handleViewByUser = useCallback(
    (digit) => {
      navigate(`${basePath}/jodi-report-users/${matchId}/${digit}`);
    },
    [navigate, basePath, matchId]
  );

  const columnDefs = useMemo(
    () => [
      slNoColumn,
      { headerName: "Digit", field: "number", maxWidth: 120, colId: "number" },
      {
        headerName: "Profit / Loss",
        field: "profit_loss",
        colId: "profit_loss",
        valueFormatter: (params) =>
          params.value != null ? Number(params.value).toFixed(2) : "0.00",
        cellClass: (params) =>
          params.value >= 0 ? "text-green-600" : "text-red-600",
      },
      {
        headerName: "Total Users",
        field: "total_users",
        colId: "total_users",
        maxWidth: 130,
      },
      {
        headerName: "Total Payout",
        field: "total_payout",
        colId: "total_payout",
        valueFormatter: (params) =>
          params.value != null ? Number(params.value).toFixed(2) : "0.00",
      },
      {
        headerName: "Actions",
        field: "actions",
        cellRenderer: ViewByUserButton,
        suppressMenu: true,
        sortable: false,
        filter: false,
        maxWidth: 150,
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
    }),
    []
  );

  const datasource = useMemo(
    () =>
      createJodiReportDatasource({
        endpoint: JODI_REPORT_ENDPOINTS.digits,
        getBasePayload: () => ({
          matchId: parseInt(matchId, 10),
          filterByResult: filterByResultRef.current,
        }),
        getSearch: () => searchRef.current,
        onMeta: (data) => {
          if (data?.match) setMatchInfo(data.match);
          if (data?.result) setResultInfo(data.result);
        },
      }),
    [matchId]
  );

  const onGridReady = useCallback(
    (params) => {
      gridRef.current = params.api;
      params.api.setGridOption("datasource", datasource);
      setGridReady(true);
    },
    [datasource]
  );

  useEffect(() => {
    searchRef.current = quickFilterText;
    if (!gridReady || !gridRef.current) return;

    const timer = setTimeout(() => {
      gridRef.current.refreshInfiniteCache();
    }, 300);

    return () => clearTimeout(timer);
  }, [quickFilterText, gridReady]);

  useEffect(() => {
    filterByResultRef.current = filterByResult;
    if (!gridReady || !gridRef.current) return;

    // The grid loads once on its own with the initial (off) state.
    if (!filterInitialisedRef.current) {
      filterInitialisedRef.current = true;
      return;
    }

    gridRef.current.paginationGoToFirstPage();
    gridRef.current.purgeInfiniteCache();
  }, [filterByResult, gridReady]);

  const openDigit = resultInfo?.open_digit ?? null;
  const closeDigit = resultInfo?.close_digit ?? null;
  const hasAnyResult = openDigit !== null || closeDigit !== null;

  const filterHint = () => {
    if (!filterByResult) return "Showing all 100 jodi numbers.";
    if (!hasAnyResult) {
      return "No result published yet — showing all 100 jodi numbers.";
    }
    if (openDigit !== null && closeDigit !== null) {
      return `Result declared (${openDigit}${closeDigit}) — showing the winning jodi only.`;
    }
    if (openDigit !== null) {
      return `Open result ${openDigit} declared — showing ${openDigit}0 – ${openDigit}9.`;
    }
    return `Close result ${closeDigit} declared — showing 0${closeDigit} – 9${closeDigit}.`;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Jodi Report</h2>
          {matchInfo ? (
            <p className="text-sm text-gray-600 mt-1">
              {matchInfo.market_name} — {matchInfo.name}
            </p>
          ) : (
            <p className="text-sm text-gray-400 mt-1">Loading match details...</p>
          )}
        </div>
        <Button
          color="default"
          variant="flat"
          onPress={() => navigate(`${basePath}/matches`)}
        >
          Back to Matches
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded border border-gray-200 bg-gray-50 px-3 py-2">
        <Switch
          size="sm"
          color="primary"
          isSelected={filterByResult}
          onChange={() => setFilterByResult((prev) => !prev)}
        >
          <span className="text-sm text-gray-800">Filter by declared result</span>
        </Switch>
        <span className="text-xs text-gray-600">{filterHint()}</span>
      </div>

      <SearchFilter
        value={quickFilterText}
        onChange={(e) => setQuickFilterText(e.target.value)}
      />

      <div className="ag-theme-alpine" style={{ height: 700, width: "100%" }}>
        <AgGridReact
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowModelType="infinite"
          cacheBlockSize={PAGE_SIZE}
          maxBlocksInCache={5}
          pagination={true}
          paginationPageSize={PAGE_SIZE}
          onGridReady={onGridReady}
          onSortChanged={(params) => params.api.refreshInfiniteCache()}
          context={{ onViewByUser: handleViewByUser }}
          enableCellTextSelection={true}
          overlayNoRowsTemplate="<span style='padding: 10px; display: block;'>No data found</span>"
        />
      </div>
    </div>
  );
};

export default JodiReportTable;
